import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  loading: boolean;
  sessionExpired: boolean;
}

interface LoginAttempt {
  timestamp: number;
  success: boolean;
  ip?: string;
}

const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    sessionExpired: false
  });

  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutEndTime, setLockoutEndTime] = useState<number | null>(null);
  const [sessionWarning, setSessionWarning] = useState(false);
  const resetTimerRef = useRef<(() => void) | null>(null);

  // Session timeout management
  useEffect(() => {
    let sessionTimer: ReturnType<typeof setTimeout>;
    let warningTimer: ReturnType<typeof setTimeout>;

    const resetSessionTimer = () => {
      clearTimeout(sessionTimer);
      clearTimeout(warningTimer);

      resetTimerRef.current = resetSessionTimer;

      if (authState.user) {
        // Warning 5 minutes before timeout
        warningTimer = setTimeout(() => {
          setSessionWarning(true);
        }, SESSION_TIMEOUT - 5 * 60 * 1000);

        // Auto logout after timeout
        sessionTimer = setTimeout(() => {
          handleSessionTimeout();
        }, SESSION_TIMEOUT);
      }
    };

    const handleSessionTimeout = async () => {
      setAuthState(prev => ({ ...prev, sessionExpired: true }));
      await supabase.auth.signOut();
    };

    // Reset timer on user activity
    const resetOnActivity = () => {
      if (authState.user) {
        resetSessionTimer();
      }
    };

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetOnActivity, true);
    });

    resetSessionTimer();

    return () => {
      clearTimeout(sessionTimer);
      clearTimeout(warningTimer);
      events.forEach(event => {
        document.removeEventListener(event, resetOnActivity, true);
      });
    };
  }, [authState.user]);

  // Check lockout status
  useEffect(() => {
    const checkLockout = () => {
      const now = Date.now();
      const recentAttempts = loginAttempts.filter(
        attempt => now - attempt.timestamp < LOCKOUT_DURATION
      );
      
      const failedAttempts = recentAttempts.filter(attempt => !attempt.success);
      
      if (failedAttempts.length >= MAX_LOGIN_ATTEMPTS) {
        const oldestFailedAttempt = failedAttempts[0];
        const lockoutEnd = oldestFailedAttempt.timestamp + LOCKOUT_DURATION;
        
        if (now < lockoutEnd) {
          setIsLocked(true);
          setLockoutEndTime(lockoutEnd);
        } else {
          setIsLocked(false);
          setLockoutEndTime(null);
          // Clean old attempts
          setLoginAttempts(prev => prev.filter(
            attempt => now - attempt.timestamp < LOCKOUT_DURATION
          ));
        }
      } else {
        setIsLocked(false);
        setLockoutEndTime(null);
      }
    };

    checkLockout();
    const interval = setInterval(checkLockout, 1000);
    return () => clearInterval(interval);
  }, [loginAttempts]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setAuthState({
          user,
          loading: false,
          sessionExpired: false
        });
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthState({
          user: null,
          loading: false,
          sessionExpired: false
        });
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthState(prev => {
        const nextUser = session?.user ?? null;
        // Bleibt derselbe Nutzer eingeloggt (z. B. TOKEN_REFRESHED beim
        // Zurückkehren in den Tab), die bestehende Objektreferenz behalten —
        // sonst laufen alle Effekte mit `user` als Dependency erneut und
        // ProtectedRoute unmountet das Dashboard (Tab/Scroll gehen verloren).
        const user = prev.user && nextUser && prev.user.id === nextUser.id ? prev.user : nextUser;
        return {
          ...prev,
          user,
          loading: false,
          sessionExpired: event === 'SIGNED_OUT' && prev.sessionExpired
        };
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  const recordLoginAttempt = (success: boolean) => {
    const attempt: LoginAttempt = {
      timestamp: Date.now(),
      success,
    };
    
    setLoginAttempts(prev => [...prev, attempt]);
    
    if (import.meta.env.DEV) {
      console.log(`Login attempt: ${success ? 'SUCCESS' : 'FAILED'} at ${new Date().toISOString()}`);
    }
  };

  const signIn = async (email: string, password: string) => {
    if (isLocked) {
      throw new Error(`Account gesperrt. Versuchen Sie es in ${Math.ceil((lockoutEndTime! - Date.now()) / 60000)} Minuten erneut.`);
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if the user has admin role in student_profiles
      const { data: profile, error: profileError } = await supabase
        .from('student_profiles')
        .select('role')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profile || profile.role !== 'admin') {
        await supabase.auth.signOut();
        throw new Error('Kein Administratorzugang. Bitte nutze den Schüler-Login.');
      }

      recordLoginAttempt(true);
      setAuthState(prev => ({ ...prev, sessionExpired: false }));
      return data;
    } catch (error) {
      recordLoginAttempt(false);
      throw error;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setAuthState({
      user: null,
      loading: false,
      sessionExpired: false
    });
  };

  const getRemainingLockoutTime = () => {
    if (!isLocked || !lockoutEndTime) return 0;
    return Math.max(0, lockoutEndTime - Date.now());
  };

  const getFailedAttempts = () => {
    const now = Date.now();
    return loginAttempts.filter(
      attempt => !attempt.success && now - attempt.timestamp < LOCKOUT_DURATION
    ).length;
  };

  const extendSession = () => {
    setSessionWarning(false);
    resetTimerRef.current?.();
  };

  const dismissSessionWarning = () => setSessionWarning(false);

  return {
    user: authState.user,
    loading: authState.loading,
    sessionExpired: authState.sessionExpired,
    isLocked,
    remainingLockoutTime: getRemainingLockoutTime(),
    failedAttempts: getFailedAttempts(),
    maxAttempts: MAX_LOGIN_ATTEMPTS,
    sessionWarning,
    extendSession,
    dismissSessionWarning,
    signIn,
    signOut,
  };
};